import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Avatar, Chip, IconButton,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, LinearProgress, Tooltip, Menu, MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreIcon,
  AttachFile as AttachIcon,
  Comment as CommentIcon,
  CheckCircle as CheckIcon,
  Schedule as TimeIcon,
  ArrowForward as MoveIcon
} from '@mui/icons-material';
import ModernLayout from '../common/ModernLayout';
import { GradientButton } from '../common/ModernButton';
import axiosInstance from '../axiosConfig';

const TaskKanban = () => {
  const [columns, setColumns] = useState([
    { id: 'pending', title: 'Por Hacer', color: '#757575', tasks: [] },
    { id: 'in_progress', title: 'En Progreso', color: '#2196F3', tasks: [] },
    { id: 'completed', title: 'Completado', color: '#4CAF50', tasks: [] }
  ]);
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);

  // Form for creating/editing task
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    estimated_hours: 0,
    assignee_id: null
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    // Organize tasks by status
    const newColumns = columns.map(col => ({
      ...col,
      tasks: tasks.filter(task => task.status === col.id)
    }));
    setColumns(newColumns);
  }, [tasks]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/tasks');
      setTasks(response.data.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async () => {
    try {
      const response = await axiosInstance.post('/api/tasks', taskForm);
      setTasks([...tasks, response.data.data]);
      setShowTaskDialog(false);
      setTaskForm({ title: '', description: '', priority: 'medium', estimated_hours: 0, assignee_id: null });
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Error al crear tarea');
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await axiosInstance.put(`/api/tasks/${taskId}`, { status: newStatus });
      setTasks(tasks.map(task => 
        task.id === taskId ? { ...task, status: newStatus } : task
      ));
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta tarea?')) return;
    
    try {
      await axiosInstance.delete(`/api/tasks/${taskId}`);
      setTasks(tasks.filter(task => task.id !== taskId));
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, columnId) => {
    e.preventDefault();
    if (draggedTask && draggedTask.status !== columnId) {
      handleUpdateTaskStatus(draggedTask.id, columnId);
    }
    setDraggedTask(null);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const TaskCard = ({ task }) => {
    const [menuAnchor, setMenuAnchor] = useState(null);
    
    return (
      <Card
        draggable
        onDragStart={(e) => handleDragStart(e, task)}
        sx={{
          mb: 2,
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-2px)',
            transition: 'all 0.2s'
          }
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Chip
              label={task.priority}
              size="small"
              color={getPriorityColor(task.priority)}
            />
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <MoreIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem onClick={() => {
                setSelectedTask(task);
                setMenuAnchor(null);
              }}>
                Ver Detalles
              </MenuItem>
              <MenuItem onClick={() => {
                handleDeleteTask(task.id);
                setMenuAnchor(null);
              }}>
                Eliminar
              </MenuItem>
            </Menu>
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {task.title}
          </Typography>

          {task.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {task.description}
            </Typography>
          )}

          {task.progress_percentage !== undefined && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption">Progreso</Typography>
                <Typography variant="caption">{task.progress_percentage}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={task.progress_percentage}
                sx={{ height: 6, borderRadius: 1 }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {task.assignee_name && (
                <Tooltip title={task.assignee_name}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.875rem' }}>
                    {task.assignee_name[0]}
                  </Avatar>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {task.estimated_hours > 0 && (
                <Chip
                  icon={<TimeIcon />}
                  label={`${task.estimated_hours}h`}
                  size="small"
                  variant="outlined"
                />
              )}
              {task.tags && task.tags.length > 0 && (
                <Chip
                  label={task.tags[0]}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          {/* Task metadata */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Tooltip title="Comentarios">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CommentIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {task.comment_count || 0}
                </Typography>
              </Box>
            </Tooltip>
            
            <Tooltip title="Adjuntos">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AttachIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {task.attachment_count || 0}
                </Typography>
              </Box>
            </Tooltip>

            {task.checklist_total > 0 && (
              <Tooltip title="Checklist">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {task.checklist_completed || 0}/{task.checklist_total}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <ModernLayout title="Tablero Kanban">
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Tablero de Tareas
          </Typography>
          <GradientButton
            startIcon={<AddIcon />}
            onClick={() => setShowTaskDialog(true)}
          >
            Nueva Tarea
          </GradientButton>
        </Box>

        {/* Kanban Columns */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 3,
            mt: 3
          }}
        >
          {columns.map(column => (
            <Box
              key={column.id}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, column.id)}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                p: 2,
                minHeight: '70vh',
                border: '2px dashed',
                borderColor: draggedTask ? 'primary.main' : 'transparent',
                transition: 'all 0.2s'
              }}
            >
              {/* Column Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  pb: 2,
                  borderBottom: 3,
                  borderColor: column.color
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: column.color
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {column.title}
                  </Typography>
                  <Chip
                    label={column.tasks.length}
                    size="small"
                    sx={{
                      bgcolor: column.color,
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </Box>
              </Box>

              {/* Tasks */}
              {loading ? (
                <LinearProgress />
              ) : column.tasks.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 4,
                    color: 'text.secondary'
                  }}
                >
                  <Typography variant="body2">
                    No hay tareas en esta columna
                  </Typography>
                </Box>
              ) : (
                column.tasks.map(task => (
                  <TaskCard key={task.id} task={task} />
                ))
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Create Task Dialog */}
      <Dialog
        open={showTaskDialog}
        onClose={() => setShowTaskDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Nueva Tarea</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Título de la tarea"
              value={taskForm.title}
              onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
              required
            />

            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={4}
              value={taskForm.description}
              onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                label="Prioridad"
                value={taskForm.priority}
                onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                sx={{ flex: 1 }}
              >
                <MenuItem value="low">Baja</MenuItem>
                <MenuItem value="medium">Media</MenuItem>
                <MenuItem value="high">Alta</MenuItem>
              </TextField>

              <TextField
                type="number"
                label="Horas estimadas"
                value={taskForm.estimated_hours}
                onChange={(e) => setTaskForm({ ...taskForm, estimated_hours: parseFloat(e.target.value) })}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTaskDialog(false)}>Cancelar</Button>
          <GradientButton
            onClick={handleCreateTask}
            disabled={!taskForm.title}
          >
            Crear Tarea
          </GradientButton>
        </DialogActions>
      </Dialog>

      {/* Task Detail Dialog (Simple version - can be expanded) */}
      {selectedTask && (
        <Dialog
          open={Boolean(selectedTask)}
          onClose={() => setSelectedTask(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{selectedTask.title}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {selectedTask.description}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label={`Prioridad: ${selectedTask.priority}`} />
              <Chip label={`Estado: ${selectedTask.status}`} />
              {selectedTask.estimated_hours > 0 && (
                <Chip label={`${selectedTask.estimated_hours} horas estimadas`} />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedTask(null)}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      )}
    </ModernLayout>
  );
};

export default TaskKanban;

